import {
  Box,
  Divider,
  Heading,
  Stack,
  Text,
  Button,
  Flex,
  AspectRatio,
  Center
} from '@chakra-ui/react';
import { format } from 'date-fns';
import Link from 'next/link';
import { urlForImage } from '@lib/sanity';
import { SaneImage } from '@components/sanity/sane-image';
import Image from 'next/image';
import { Post } from './listing';

interface Props {
  post: Post;
}

export function TopCard({ post }: Props): JSX.Element {
  const formattedDate =
    post.date && format(new Date(post.date), 'MMM dd, yyyy');
  const url = urlForImage(post.coverImage?.asset).url() || '';

  return (
    <Stack>
      <Link legacyBehavior href={post.slug} passHref>
        <Flex
          as="a"
          color="gray.900"
          flexDir={{ base: 'column', lg: 'row' }}
          zIndex={10}
          shadow="md"
          borderRadius="12px"
          gap={{ base: 4, lg: 12 }}
          w="100%"
          py="24px"
        >
          <Flex
            w="auto"
            flexDir={'column'}
            order={{ base: 2, lg: 1 }}
            justifyContent="space-evenly"
            py="24px"
            color="white"
          >
            <Heading
              as="h4"
              size="sm"
              color="primary.500"
              mb={{ base: '8px', lg: 0 }}
              bg="primary.25"
              maxW="fit-content"
              padding="6px 12px"
              gap="8px"
              borderRadius={'999px'}
            >
              {post.category}
            </Heading>
            <Heading as="h1" mb="24px" pt="16px" color="white" size="h4">
              {post.title}
            </Heading>
            <Text lineHeight="28px">{post.excerpt}</Text>

            <Stack justifyContent="space-between" w="100%">
              <Flex align="center" py="24px" w="100%" gap={4}>
                <SaneImage
                  className="block h-10 w-10 rounded-full"
                  alt={post.author.name}
                  src={post.author.picture}
                  height={40}
                  width={40}
                  quality={100}
                />
                <Flex
                  direction={{ base: 'column', sm: 'row' }}
                  align={{ base: 'flex-start', sm: 'center' }}
                  gap={{ base: 0, sm: 4 }}
                >
                  <Text>{post.author.name}</Text>

                  <Divider
                    h="28px"
                    orientation="vertical"
                    display={{ base: 'none', sm: 'block' }}
                  />

                  <Text textColor={'dark-text'}>{formattedDate}</Text>
                </Flex>
              </Flex>

              <Box>
                <Button
                  border="1px solid #E4E9F2"
                  variant={'white'}
                  w="auto"
                  minW="225px"
                  fontSize={'md'}
                >
                  Read More
                </Button>
              </Box>
            </Stack>
          </Flex>

          <Center order={{ base: 1, lg: 2 }}>
            <AspectRatio
              w={{ base: 'full', md: 'full', lg: '630px' }}
              ratio={1200 / 630}
            >
              {post.coverImage && (
                <Box borderRadius={'12px'}>
                  <Image alt={post.title} src={url} fill />
                </Box>
              )}
            </AspectRatio>
          </Center>
        </Flex>
      </Link>
    </Stack>
  );
}
